import React, { useState } from 'react';

interface PersonaDetailProps {
  detail: {
    name: string;
    description: string;
    completed_persona: string;
    research_report?: string;
    created_at: string;
    in_conductor_config: boolean;
    chat_count: number;
  } | null;
  loading: boolean;
  onBack: () => void;
}

const PersonaDetail: React.FC<PersonaDetailProps> = ({ detail, loading, onBack }) => {
  const [showResearch, setShowResearch] = useState(false);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-xl text-gray-600">Loading persona details...</div>
      </div>
    );
  }

  if (!detail) {
    return (
      <div className="p-4 text-yellow-600 bg-yellow-100 rounded-md">
        No persona detail available
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={onBack}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            ← Back to List
          </button>
          <h2 className="text-2xl font-bold">{detail.name}</h2>
        </div>
      </div>

      {/* Status Badges and Metadata */}
      <div className="flex items-center space-x-3">
        <span
          className={`px-3 py-1 text-sm font-semibold rounded-full ${
            detail.in_conductor_config
              ? 'bg-green-100 text-green-800'
              : 'bg-blue-100 text-blue-800'
          }`}
        >
          {detail.in_conductor_config ? '🟢 Available to Users' : '🔵 Database Only'}
        </span>
        <span className="px-3 py-1 text-sm font-semibold text-gray-700 bg-gray-100 rounded-full">
          Used in {detail.chat_count} chat{detail.chat_count !== 1 ? 's' : ''}
        </span>
        <span className="text-sm text-gray-500">
          Created: {new Date(detail.created_at).toLocaleDateString()}
        </span>
      </div>

      {/* Description */}
      <div className="p-6 bg-white rounded-lg shadow-md">
        <h3 className="mb-2 text-lg font-semibold">Description</h3>
        <p className="text-gray-700">{detail.description}</p>
      </div>

      {/* Chat Prompt */}
      <div className="p-6 bg-white rounded-lg shadow-md">
        <h3 className="mb-4 text-lg font-semibold">Chat Prompt</h3>
        <div className="p-4 overflow-auto bg-gray-50 border border-gray-200 rounded-md max-h-96">
          <pre className="text-sm font-mono text-gray-800 whitespace-pre-wrap">
            {detail.completed_persona}
          </pre>
        </div>
      </div>

      {/* Research Report (Collapsible) */}
      {detail.research_report && (
        <div className="p-6 bg-white rounded-lg shadow-md">
          <button
            onClick={() => setShowResearch(!showResearch)}
            className="flex items-center justify-between w-full text-left"
          >
            <h3 className="text-lg font-semibold">Research Report</h3>
            <span className="text-gray-500">
              {showResearch ? '▼' : '▶'}
            </span>
          </button>
          {showResearch && (
            <div className="p-4 mt-4 overflow-auto bg-gray-50 border border-gray-200 rounded-md max-h-96">
              <pre className="text-sm text-gray-800 whitespace-pre-wrap">
                {detail.research_report}
              </pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PersonaDetail;
